// ══════════════════════════════════════════════════════════════
// MIXUP BR - LOJISTA - DEVO ENTREGADORES
// → C:\projetos\mixup-lojista\src\pages\DevoEntregadores.jsx
// ──────────────────────────────────────────────────────────────
// Mostra ao lojista quanto ele deve a cada entregador, separado
// em: Pendentes (sem decisão), Aguardando (entregador pediu),
// Pagos. Permite marcar entregas como pagas individualmente.
// ══════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../ThemeContext";
import { get, put } from "../api";

export default function DevoEntregadores({ lojaId, lang = "pt" }) {
  const { theme: C } = useTheme();
  const [dados, setDados] = useState({ totais: {}, entregadores: [], entregas: [] });
  const [de, setDe] = useState(() => new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10));
  const [ate, setAte] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState("todos"); // todos, pendente, aguardando, pago
  const [expandido, setExpandido] = useState(null);
  const [marcando, setMarcando] = useState(null);

  const fmt = (v) => "R$ " + Number(v || 0).toFixed(2).replace(".", ",");

  const fetchDados = useCallback(async () => {
    if (!lojaId) return;
    setLoading(true);
    try {
      const r = await get(`/relatorios/?mod=devo_entregadores_loja&loja_id=${lojaId}&de=${de}&ate=${ate}`);
      if (r && r.sucesso) {
        setDados({
          totais: r.totais || {},
          entregadores: r.entregadores || [],
          entregas: r.entregas || [],
        });
      }
    } catch (e) {
      console.warn("DevoEntregadores fetch:", e);
    }
    setLoading(false);
  }, [lojaId, de, ate]);

  useEffect(() => { fetchDados(); }, [fetchDados]);

  const marcarEntrega = async (pedidoId, campo, valor) => {
    setMarcando(pedidoId);
    try {
      const body = { [campo]: valor ? 1 : 0 };
      if (campo === "entregador_pago" && valor) body.entregador_aguardando_loja = 0;
      if (campo === "entregador_aguardando_loja" && valor) body.entregador_pago = 0;
      await put(`/pedidos_entregador/?id=${pedidoId}`, body);
      await fetchDados();
    } catch (e) {
      alert("Erro ao atualizar: " + e.message);
    }
    setMarcando(null);
  };

  // Status de uma entrega
  const statusEntrega = (e) => {
    if (parseInt(e.entregador_pago) === 1) return "pago";
    if (parseInt(e.entregador_aguardando_loja) === 1) return "aguardando";
    return "pendente";
  };

  // Entregas filtradas por status (se aplicável)
  const filtrarEntregas = (entregas) => {
    if (filtroStatus === "todos") return entregas;
    return entregas.filter(e => statusEntrega(e) === filtroStatus);
  };

  // Entregas agrupadas por entregador
  const entregasPorEntregador = (entregadorId) => {
    return filtrarEntregas(dados.entregas.filter(e => String(e.entregador_id) === String(entregadorId)));
  };

  return (
    <div style={{ animation: "fadeUp .4s ease-out" }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4, color: C.text }}>
        💸 Devo aos Entregadores
      </h2>
      <p style={{ fontSize: 13, color: C.textSoft, marginBottom: 20 }}>
        Acompanhe o que sua loja deve a cada entregador. Marque "Pago" quando quitar.
      </p>

      {/* Filtros de período */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "end" }}>
        <div>
          <label style={{ display: "block", fontSize: 11, color: C.textSoft, fontWeight: 700, marginBottom: 4 }}>De</label>
          <input type="date" value={de} onChange={e => setDe(e.target.value)}
            style={{ padding: "8px 10px", background: C.card, border: "1px solid " + C.border, borderRadius: 8, color: C.text, fontSize: 13 }} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, color: C.textSoft, fontWeight: 700, marginBottom: 4 }}>Até</label>
          <input type="date" value={ate} onChange={e => setAte(e.target.value)}
            style={{ padding: "8px 10px", background: C.card, border: "1px solid " + C.border, borderRadius: 8, color: C.text, fontSize: 13 }} />
        </div>
        <button onClick={fetchDados} disabled={loading}
          style={{ padding: "10px 18px", background: C.red, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700, opacity: loading ? 0.6 : 1 }}>
          {loading ? "..." : "🔄 Atualizar"}
        </button>
      </div>

      {/* Cards de totais */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 20 }}>
        <div style={{ background: C.card, borderRadius: 12, padding: 14, border: "1px solid " + C.border }}>
          <div style={{ fontSize: 10, color: C.textSoft, fontWeight: 700, letterSpacing: 0.5 }}>TOTAL ENTREGAS</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginTop: 4 }}>{dados.totais?.total_entregas || 0}</div>
          <div style={{ fontSize: 11, color: C.textSoft, marginTop: 2 }}>{fmt(dados.totais?.valor_total)}</div>
        </div>
        <div style={{ background: "#FFB74D14", borderRadius: 12, padding: 14, border: "1px solid #FFB74D40" }}>
          <div style={{ fontSize: 10, color: "#FFB74D", fontWeight: 700, letterSpacing: 0.5 }}>● PENDENTES</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#FFB74D", marginTop: 4 }}>{fmt(dados.totais?.valor_pendente)}</div>
          <div style={{ fontSize: 11, color: C.textSoft, marginTop: 2 }}>{dados.totais?.qtd_pendentes || 0} entrega(s)</div>
        </div>
        <div style={{ background: "#42A5F514", borderRadius: 12, padding: 14, border: "2px solid #42A5F5" }}>
          <div style={{ fontSize: 10, color: "#42A5F5", fontWeight: 700, letterSpacing: 0.5 }}>⏳ AGUARDANDO LOJA</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#42A5F5", marginTop: 4 }}>{fmt(dados.totais?.valor_aguardando)}</div>
          <div style={{ fontSize: 11, color: C.textSoft, marginTop: 2 }}>{dados.totais?.qtd_aguardando || 0} entrega(s) — pagar agora</div>
        </div>
        <div style={{ background: "#10B98114", borderRadius: 12, padding: 14, border: "1px solid #10B98140" }}>
          <div style={{ fontSize: 10, color: "#10B981", fontWeight: 700, letterSpacing: 0.5 }}>✓ PAGOS</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#10B981", marginTop: 4 }}>{fmt(dados.totais?.valor_pago)}</div>
          <div style={{ fontSize: 11, color: C.textSoft, marginTop: 2 }}>{dados.totais?.qtd_pagas || 0} entrega(s)</div>
        </div>
      </div>

      {/* Filtros de status */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          { k: "todos", l: "Todas", cor: C.text },
          { k: "aguardando", l: "⏳ Aguardando Loja", cor: "#42A5F5" },
          { k: "pendente", l: "● Pendentes", cor: "#FFB74D" },
          { k: "pago", l: "✓ Pagos", cor: "#10B981" },
        ].map(f => (
          <button key={f.k} onClick={() => setFiltroStatus(f.k)}
            style={{
              padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer",
              border: "1px solid " + (filtroStatus === f.k ? f.cor : C.border),
              background: filtroStatus === f.k ? f.cor + "20" : C.card,
              color: filtroStatus === f.k ? f.cor : C.textSoft,
            }}>
            {f.l}
          </button>
        ))}
      </div>

      {/* Lista de entregadores */}
      {dados.entregadores.length === 0 && !loading && (
        <div style={{ background: C.card, borderRadius: 14, padding: 40, textAlign: "center", border: "1px solid " + C.border }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
          <div style={{ fontSize: 14, color: C.textSoft, fontWeight: 600 }}>Nenhuma entrega no período</div>
        </div>
      )}

      {dados.entregadores.map(ent => {
        const ehExpandido = expandido === ent.entregador_id;
        const entregas = entregasPorEntregador(ent.entregador_id);
        const aguardando = parseFloat(ent.valor_aguardando) > 0;

        // Se filtro ativo e entregador não tem entregas naquele status, esconde
        if (filtroStatus !== "todos" && entregas.length === 0) return null;

        return (
          <div key={ent.entregador_id} style={{
            background: C.card, borderRadius: 14, marginBottom: 10,
            border: "1px solid " + (aguardando ? "#42A5F540" : C.border),
            borderLeft: "4px solid " + (aguardando ? "#42A5F5" : parseFloat(ent.valor_pendente) > 0 ? "#FFB74D" : "#10B981"),
            overflow: "hidden",
          }}>
            <div onClick={() => setExpandido(ehExpandido ? null : ent.entregador_id)}
              style={{ padding: 16, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
                {ent.foto_url ? (
                  <img src={ent.foto_url} alt="" style={{ width: 44, height: 44, borderRadius: 22, objectFit: "cover" }} />
                ) : (
                  <div style={{ width: 44, height: 44, borderRadius: 22, background: C.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>👤</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{ent.entregador_nome}</div>
                  <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
                    {parseFloat(ent.valor_aguardando) > 0 && (
                      <span style={{ fontSize: 11, color: "#42A5F5", fontWeight: 700 }}>⏳ {ent.qtd_aguardando} aguardando</span>
                    )}
                    {parseFloat(ent.valor_pendente) > 0 && (
                      <span style={{ fontSize: 11, color: "#FFB74D", fontWeight: 700 }}>● {ent.qtd_pendentes} pendentes</span>
                    )}
                    {parseFloat(ent.valor_pago) > 0 && (
                      <span style={{ fontSize: 11, color: "#10B981", fontWeight: 700 }}>✓ {ent.qtd_pagas} pagas</span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: aguardando ? "#42A5F5" : C.text }}>
                  {fmt((parseFloat(ent.valor_aguardando) || 0) + (parseFloat(ent.valor_pendente) || 0))}
                </div>
                <div style={{ fontSize: 10, color: C.textSoft, marginTop: 2 }}>a pagar</div>
              </div>
              <div style={{ fontSize: 18, color: C.textSoft }}>{ehExpandido ? "▲" : "▼"}</div>
            </div>

            {ehExpandido && (
              <div style={{ borderTop: "1px solid " + C.border, padding: 12, background: C.bg }}>
                {entregas.length === 0 && (
                  <div style={{ padding: 20, textAlign: "center", color: C.textSoft, fontSize: 13 }}>Nenhuma entrega no filtro atual</div>
                )}
                {entregas.map(e => {
                  const status = statusEntrega(e);
                  const isMarcando = marcando === e.id;
                  return (
                    <div key={e.id} style={{
                      background: C.card, borderRadius: 10, padding: 12, marginBottom: 8,
                      border: "1px solid " + (status === "aguardando" ? "#42A5F540" : status === "pago" ? "#10B98140" : C.border),
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                            🛵 #{e.codigo || e.id}
                          </div>
                          <div style={{ fontSize: 11, color: C.textSoft, marginTop: 2 }}>
                            {(e.criado_em || "").slice(0, 16).replace("T", " ")} • {(e.forma_pagamento || "").toUpperCase()}
                          </div>
                          {status === "pago" && e.entregador_pago_em && (
                            <div style={{ fontSize: 10, color: "#10B981", marginTop: 2, fontWeight: 600 }}>
                              ✓ Pago em {(e.entregador_pago_em || "").slice(0, 16).replace("T", " ")}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: status === "pago" ? "#10B981" : status === "aguardando" ? "#42A5F5" : "#FFB74D" }}>
                          {fmt(e.taxa_entrega)}
                        </div>
                      </div>

                      {status === "aguardando" && (
                        <div style={{ background: "#42A5F520", borderRadius: 6, padding: 6, marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: "#42A5F5", fontWeight: 700, textAlign: "center" }}>
                            ⏳ Entregador aguarda pagamento — Pagar agora
                          </div>
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 6 }}>
                        {status !== "pago" && (
                          <button onClick={() => marcarEntrega(e.id, "entregador_pago", true)} disabled={isMarcando}
                            style={{ flex: 1, padding: "8px 10px", border: "none", borderRadius: 8, background: "#10B981", color: "#fff", fontSize: 11, fontWeight: 800, cursor: "pointer", opacity: isMarcando ? 0.5 : 1 }}>
                            ✓ Marcar como Pago
                          </button>
                        )}
                        {status === "pago" && (
                          <button onClick={() => marcarEntrega(e.id, "entregador_pago", false)} disabled={isMarcando}
                            style={{ flex: 1, padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 8, background: "transparent", color: C.textSoft, fontSize: 11, fontWeight: 700, cursor: "pointer", opacity: isMarcando ? 0.5 : 1 }}>
                            Desfazer
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
